/**
 * Single source of truth for plan limits, feature gates, and effective-plan logic.
 * Every limit / feature check in the app should import from here.
 *
 * Mirrors the landing page Pricing section. When subscription lapses
 * (canceled / unpaid / past_due), the effective plan falls back to Free.
 */

export type Plan = 'Free' | 'Basic' | 'Pro' | 'Business';

/** Hard numeric limits per plan. */
export const PLAN_LIMITS = {
    Free:     { properties: 1,        tenants: 2,        documents: 3        },
    Basic:    { properties: 5,        tenants: Infinity, documents: 20       },
    Pro:      { properties: 20,       tenants: Infinity, documents: 100      },
    Business: { properties: Infinity, tenants: Infinity, documents: Infinity },
} as const;

/**
 * Feature flags derived from the landing page Pricing copy:
 *   Free     — 1 property, 2 tenants, tenant portal, basic rent tracking, email support
 *   Basic    — + chat, maintenance, rent reminders
 *   Pro      — + advanced reports, document e-signing, priority support
 *   Business — + team collaboration, API access, dedicated support
 */
export const PLAN_FEATURES = {
    Free: {
        tenantPortal:    true,
        basicRent:       true,
        chat:            false,
        maintenance:     false,
        rentReminders:   false,
        esigning:        false,
        advancedReports: false,
        teamCollab:      false,
        apiAccess:       false,
        prioritySupport: false,
    },
    Basic: {
        tenantPortal:    true,
        basicRent:       true,
        chat:            true,
        maintenance:     true,
        rentReminders:   true,
        esigning:        false,
        advancedReports: false,
        teamCollab:      false,
        apiAccess:       false,
        prioritySupport: false,
    },
    Pro: {
        tenantPortal:    true,
        basicRent:       true,
        chat:            true,
        maintenance:     true,
        rentReminders:   true,
        esigning:        true,
        advancedReports: true,
        teamCollab:      false,
        apiAccess:       false,
        prioritySupport: true,
    },
    Business: {
        tenantPortal:    true,
        basicRent:       true,
        chat:            true,
        maintenance:     true,
        rentReminders:   true,
        esigning:        true,
        advancedReports: true,
        teamCollab:      true,
        apiAccess:       true,
        prioritySupport: true,
    },
} as const;

export type Feature = keyof typeof PLAN_FEATURES.Free;

/**
 * Stripe sub statuses that mean "user is not currently paying".
 * When in one of these states, effective plan drops to Free regardless of
 * the stored `plan` column (defence in depth — primary enforcement is the
 * Stripe webhook, this is a client-side safety net).
 */
const LAPSED_STATUSES = new Set([
    'canceled',
    'incomplete_expired',
    'unpaid',
    'past_due',
]);

/**
 * Resolve the effective plan for a user.
 * - If plan is "Free", return Free.
 * - If subscription has lapsed (canceled/unpaid/etc.), return Free.
 * - Otherwise return the stored plan.
 */
export function getEffectivePlan(
    plan: string | null | undefined,
    subscriptionStatus: string | null | undefined,
): Plan {
    const p = (plan || 'Free') as Plan;
    if (p === 'Free') return 'Free';
    if (subscriptionStatus && LAPSED_STATUSES.has(subscriptionStatus)) return 'Free';
    return p;
}

export function getPlanLimits(plan: string) {
    return PLAN_LIMITS[plan as Plan] ?? PLAN_LIMITS.Free;
}

export function getPlanFeatures(plan: string) {
    return PLAN_FEATURES[plan as Plan] ?? PLAN_FEATURES.Free;
}

export function hasFeature(plan: string, feature: Feature): boolean {
    return !!getPlanFeatures(plan)[feature];
}

/** Minimum plan required to unlock a given feature (for upgrade prompts). */
export function minPlanFor(feature: Feature): Plan {
    const order: Plan[] = ['Free', 'Basic', 'Pro', 'Business'];
    for (const p of order) {
        if (PLAN_FEATURES[p][feature]) return p;
    }
    return 'Business';
}

export function formatLimit(n: number): string {
    return n === Infinity ? 'Unlimited' : String(n);
}

/** Returns a pct 0–100 for a usage bar (caps at 100). */
export function usagePct(used: number, limit: number): number {
    if (limit === Infinity) return Math.min((used / 10) * 100, 8); // tiny dot for unlimited
    return Math.min(Math.round((used / limit) * 100), 100);
}
